"use client"

import type { Transition, Variants } from "motion/react"
import { motion, useAnimation } from "motion/react"
import type { HTMLAttributes } from "react"
import * as React from "react"
import { forwardRef, useCallback, useImperativeHandle } from "react"

import { cn } from "@/lib/utils"
import { useHoverResetGuard } from "./useHoverResetGuard"

export interface SlidersHorizontalIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

interface SlidersHorizontalIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number
  /** e.g. `inherit` from sidebar nav so stroke follows the row color */
  color?: string
}

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 100,
  damping: 12,
  mass: 0.4,
}

const SLIDER_LINE_1_X2: Variants = {
  normal: { x2: 14 },
  animate: { x2: 10 },
}

const SLIDER_LINE_2_X1: Variants = {
  normal: { x1: 10 },
  animate: { x1: 5 },
}

const SLIDER_LINE_3_X2: Variants = {
  normal: { x2: 12 },
  animate: { x2: 18 },
}

const SLIDER_LINE_4_X1: Variants = {
  normal: { x1: 8 },
  animate: { x1: 13 },
}

const SLIDER_LINE_5_X2: Variants = {
  normal: { x2: 12 },
  animate: { x2: 4 },
}

const SLIDER_LINE_6_X1: Variants = {
  normal: { x1: 16 },
  animate: { x1: 8 },
}

const SLIDER_LINE_7: Variants = {
  normal: { x1: 14, x2: 14 },
  animate: { x1: 9, x2: 9 },
}

const SLIDER_LINE_8: Variants = {
  normal: { x1: 8, x2: 8 },
  animate: { x1: 14, x2: 14 },
}

const SLIDER_LINE_9: Variants = {
  normal: { x1: 16, x2: 16 },
  animate: { x1: 8, x2: 8 },
}

const SlidersHorizontalIcon = forwardRef<
  SlidersHorizontalIconHandle,
  SlidersHorizontalIconProps
>(
  (
    { onMouseEnter, onMouseLeave, className, size = 28, color, style, ...props },
    ref
  ) => {
    const controls = useAnimation()
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } =
      useHoverResetGuard()

    const runSlidersAnimation = useCallback(async () => {
      await controls.start("normal", { duration: 0 })
      await controls.start("animate")
    }, [controls])

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () => void withLockedHoverReset(runSlidersAnimation),
        stopAnimation: () => {
          releaseHoverResetLock()
          void controls.start("normal")
        },
      }),
      [controls, releaseHoverResetLock, runSlidersAnimation, withLockedHoverReset]
    )

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e)
        void runSlidersAnimation()
      },
      [onMouseEnter, runSlidersAnimation]
    )

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e)
        if (!shouldApplyHoverReset()) return
        void controls.start("normal")
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    )

    return (
      <div
        className={cn("inline-flex shrink-0 items-center justify-center", className)}
        style={{
          color: color && color !== "inherit" ? color : undefined,
          ...style,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_1_X2}
            x1="21"
            x2="14"
            y1="4"
            y2="4"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_2_X1}
            x1="10"
            x2="3"
            y1="4"
            y2="4"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_3_X2}
            x1="21"
            x2="12"
            y1="12"
            y2="12"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_4_X1}
            x1="8"
            x2="3"
            y1="12"
            y2="12"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_5_X2}
            x1="3"
            x2="12"
            y1="20"
            y2="20"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_6_X1}
            x1="16"
            x2="21"
            y1="20"
            y2="20"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_7}
            x1="14"
            x2="14"
            y1="2"
            y2="6"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_8}
            x1="8"
            x2="8"
            y1="10"
            y2="14"
          />
          <motion.line
            animate={controls}
            initial="normal"
            transition={DEFAULT_TRANSITION}
            variants={SLIDER_LINE_9}
            x1="16"
            x2="16"
            y1="18"
            y2="22"
          />
        </svg>
      </div>
    )
  }
)

SlidersHorizontalIcon.displayName = "SlidersHorizontalIcon"

export { SlidersHorizontalIcon }
